import React, { useState, useEffect } from 'react';

import Select from 'react-select';
import queryString from 'query-string';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import MFAFlow from './MFAFlow';
import ResetPasswordForm from './ResetPasswordForm';
import ShowPasswordByIcon from '../../common/ShowPasswordByIcon';
import { NextLogo } from '../../theme/images';
import { FormContainer } from '../../theme/Global';
import { LOGIN_STEPS } from '../../constants/UserConstants';
import { clearErrorMessage, login } from '../../store/actions/userState';
import { getCustomerNames, getEmail, getVerifyPassword } from '../../api';
import {
  Button,
  ErrorMsg,
  InputFormField,
  PageLoader,
  SuccessMsg,
  ContractInputSelect,
} from '../../common';
import {
  pageTitleConditions,
  PATH_AMAZON_ACCOUNT,
  PATH_AMAZON_MERCHANT,
  PATH_BILLING_DETAILS,
  PATH_COMPANY_DETAILS,
  PATH_CUSTOMER_DETAILS,
  PATH_CUSTOMER_LIST,
  PATH_SUMMARY,
} from '../../constants';

export default function Login() {
  const history = useHistory();
  const dispatch = useDispatch();
  const { register, handleSubmit, errors, watch } = useForm();
  const loader = useSelector((state) => state.userState.isLoading);
  const apiError = useSelector((state) => state.userState.error);
  const resetPasswordMsg = useSelector((state) => state.userState.showResetMsg);
  const [isPasswordEyeOpen, setIsPasswordEyeOpen] = useState(false);
  const [customerApiError, setCustomerApiError] = useState([]);
  const [isLoading, setIsLoading] = useState({ loader: false, type: 'button' });
  const [customerNames, setCustomerNames] = useState([]);
  const [getName, setGetName] = useState({ email: '', customer: '' });
  const [forgotApiError, setforgotApiError] = useState([]);
  const params = queryString.parse(history.location.search);
  const role = localStorage.getItem('role') || '';
  const customerId = localStorage.getItem('customer') || '';
  const step = JSON.parse(localStorage.getItem('step')) || '';
  const [isMfaAlreadyScanned, setIsMfaAlreadyScanned] = useState(false);
  const [loginResponse, setLoginResponse] = useState(null);
  const [userData, setUserData] = useState({
    email: '',
    password: '',
    customer: '',
  });

  const isMFATimerExists = localStorage.getItem('mfaTimerStart');

  const [currentStep, setCurrentStep] = useState(
    isMFATimerExists ? LOGIN_STEPS.MFA : LOGIN_STEPS.EMAIL,
  );

  const handleBackStep = () => {
    switch (currentStep) {
      case LOGIN_STEPS.RESET_PASSWORD:
        setCurrentStep(LOGIN_STEPS.EMAIL);
        break;

      case LOGIN_STEPS.MFA:
        setCurrentStep(LOGIN_STEPS.PASSWORD);
        break;

      case LOGIN_STEPS.PASSWORD:
        if (customerNames?.length > 1) {
          setCurrentStep(LOGIN_STEPS.ACCOUNT_SELECTION);
        } else {
          setCurrentStep(LOGIN_STEPS.EMAIL);
        }
        break;

      case LOGIN_STEPS.ACCOUNT_SELECTION:
        setCurrentStep(LOGIN_STEPS.EMAIL);
        break;

      default:
        break;
    }
  };

  const getHeaderTitle = () => {
    switch (currentStep) {
      case LOGIN_STEPS.EMAIL:
        return 'Sign in';
      case LOGIN_STEPS.ACCOUNT_SELECTION:
        return 'Select Account';
      case LOGIN_STEPS.PASSWORD:
        return 'Welcome Back!';
      case LOGIN_STEPS.MFA:
        return 'Two-Factor Authentication';
      default:
        return 'Sign in';
    }
  };

  // Call the function if only page url is get changed (Code to add page title)
  useEffect(() => {
    pageTitleConditions(history.location);
    history.listen((location) => {
      pageTitleConditions(location);
    });
  }, [history]);

  useEffect(() => {
    if (localStorage.getItem('token') && !isMFATimerExists) {
      if (role.includes('Customer') || role === 'Customer') {
        const id =
          step &&
          Object.keys(step) &&
          Object.keys(step).find((op) => op === customerId);
        if (step === null || id === '' || id === null)
          history.push(PATH_COMPANY_DETAILS);
        if (step[id] === 1) history.push(PATH_COMPANY_DETAILS);
        if (step[id] === 2) history.push(PATH_BILLING_DETAILS);
        if (step[id] === 3) history.push(PATH_AMAZON_MERCHANT);
        if (step[id] === 4) history.push(PATH_AMAZON_ACCOUNT);
        if (step[id] === 5) history.push(PATH_SUMMARY);
        if (step[id] === 6)
          history.push(PATH_CUSTOMER_DETAILS.replace(':id', id));
      } else history.push(PATH_CUSTOMER_LIST);
    }
  }, [history, role, customerId, step, dispatch, isMFATimerExists]);

  const checkPasswordExpiry = (data) => {
    if (!data || Object.keys(data).length === 0) return false;

    if (!data.password_changed_at) return true;

    const lastChanged = new Date(data.password_changed_at);
    const today = new Date();

    // Calculate difference in days
    const diffTime = Math.abs(today - lastChanged);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > 365;
  };

  const onSubmit = async (data) => {
    setIsLoading({ loader: true, type: 'button' });

    switch (currentStep) {
      case LOGIN_STEPS.EMAIL: {
        setGetName({ ...getName, email: data.email });

        if (params && params.customer && params.step) {
          setGetName({
            email: data.email,
            customer: params && params.customer,
          });

          localStorage.setItem('customer', params?.customer);

          setCurrentStep(LOGIN_STEPS.PASSWORD);
          setIsLoading({ loader: false, type: 'button' });
          return;
        }

        getCustomerNames(data.email).then(async (response) => {
          if (response?.status === 200) {
            const verifyRes = await getVerifyPassword(data.email);
            if (verifyRes?.status === 200) {
              const isExpired = checkPasswordExpiry(verifyRes.data);
              if (isExpired) {
                setCurrentStep(LOGIN_STEPS.RESET_PASSWORD);
                setIsLoading({ loader: false, type: 'button' });
                return;
              }

              const customerData = response?.data;

              if (!customerData || customerData.length === 0) {
                setCurrentStep(LOGIN_STEPS.PASSWORD);
              } else if (customerData.length === 1) {
                setCustomerNames(customerData);
                setGetName({
                  email: data.email,
                  customer: customerData[0]?.value,
                });
                setCurrentStep(LOGIN_STEPS.PASSWORD);
              } else {
                setCustomerNames(customerData);
                setCurrentStep(LOGIN_STEPS.ACCOUNT_SELECTION);
              }
              setIsLoading({ loader: false, type: 'button' });
            }
          } else {
            setCustomerApiError(response?.data);
            setIsLoading({ loader: false, type: 'button' });
          }
        });
        break;
      }
      case LOGIN_STEPS.ACCOUNT_SELECTION: {
        setCurrentStep(LOGIN_STEPS.PASSWORD);
        setIsLoading({ loader: false, type: 'button' });
        break;
      }
      case LOGIN_STEPS.PASSWORD: {
        const detail = {
          ...data,
          email: getName.email,
        };

        setIsLoading({ loader: true, type: 'button' });

        dispatch(login(detail, { customer: getName.customer }))
          .then((response) => {
            const loginData = response?.data;
            setLoginResponse(loginData);

            if (!loginData) return;

            if (loginData.user && loginData.user.is_mfa_enabled === false) {
              setCurrentStep(LOGIN_STEPS.MFA);
              setIsMfaAlreadyScanned(false);
              setIsLoading({ loader: false, type: 'button' });
            } else if (loginData?.mfa_required) {
              setIsMfaAlreadyScanned(true);
              setCurrentStep(LOGIN_STEPS.MFA);
              setUserData((prev) => ({
                ...prev,
                ...detail,
                customer: getName.customer,
              }));
              setIsLoading({ loader: false, type: 'button' });
            } else {
              setIsLoading({ loader: false, type: 'button' });
            }
          })
          .catch((error) => {
            toast.error(
              error.message || 'Login failed. Please check your credentials.',
            );
            setIsLoading({ loader: false, type: 'button' });
          });

        break;
      }

      default:
        setIsLoading({ loader: false, type: 'button' });
        break;
    }
  };

  const handleChange = () => {
    dispatch(clearErrorMessage());
    setCustomerApiError([]);
    setforgotApiError([]);
  };

  const forgotPassword = () => {
    setIsLoading({ loader: true, type: 'forgot' });

    getEmail({ email: getName.email }).then((response) => {
      if (response?.status === 400) {
        setforgotApiError(response?.data?.[0]);
        setIsLoading({ loader: false, type: 'forgot' });

        setCurrentStep(LOGIN_STEPS.EMAIL);
      } else {
        toast.success(
          'We have emailed you a reset link, please check your email.',
        );

        setCurrentStep(LOGIN_STEPS.PASSWORD);

        setIsLoading({ loader: false, type: 'forgot' });
      }
    });
  };

  const handlePasswordResetSuccess = () => {
    setCurrentStep(LOGIN_STEPS.EMAIL);
  };

  return (
    <>
      <FormContainer>
        <ToastContainer
          position="top-center"
          autoClose={5000}
          pauseOnFocusLoss={false}
        />
        <div className="container-fluid">
          <div className="row h-100">
            <div className="col-lg-6 pl-0 pr-0 ">
              <div className="banner-bg  d-lg-block d-none" />
            </div>
            <div className="col-lg-6 pl-0 pr-0 h-100">
              <div className=" inner-langing-form grey-overlay">
                <div className=" inner-body">
                  <div className="landing-page-logo">
                    <img src={NextLogo} alt="logo " />
                  </div>

                  <div className="login-success-msg">
                    {resetPasswordMsg && (
                      <SuccessMsg message="New Password set!" />
                    )}
                  </div>
                  {currentStep !== LOGIN_STEPS.EMAIL &&
                    currentStep !== LOGIN_STEPS.MFA && (
                      <div className="row">
                        <div className="col-12">
                          <div
                            role="presentation"
                            className="back-link"
                            onClick={handleBackStep}
                          >
                            <FontAwesomeIcon
                              icon="fa-regular fa-arrow-left"
                              className="ml-1 mr-2"
                              style={{ fontSize: '12px', color: '#ff4817' }}
                            />
                            Back a step
                          </div>
                        </div>
                      </div>
                    )}

                  {(() => {
                    switch (currentStep) {
                      case LOGIN_STEPS.RESET_PASSWORD:
                        return (
                          <ResetPasswordForm
                            isPasswordExpired
                            userData={getName}
                            onResetSuccess={handlePasswordResetSuccess}
                          />
                        );

                      case LOGIN_STEPS.MFA:
                        return (
                          <MFAFlow
                            isAlreadyScanned={isMfaAlreadyScanned}
                            userData={userData}
                            loginResponse={loginResponse}
                          />
                        );

                      case LOGIN_STEPS.EMAIL:
                      case LOGIN_STEPS.ACCOUNT_SELECTION:
                      case LOGIN_STEPS.PASSWORD:
                        return (
                          <>
                            <h2
                              className={
                                currentStep === LOGIN_STEPS.EMAIL
                                  ? 'mb-4 mt-3'
                                  : 'mb-0 mt-3'
                              }
                            >
                              {getHeaderTitle()}
                            </h2>

                            {currentStep === LOGIN_STEPS.PASSWORD && (
                              <p className="sub-text">
                                Please verify that it&apos;s you.
                              </p>
                            )}
                            {currentStep === LOGIN_STEPS.ACCOUNT_SELECTION && (
                              <p className="sub-text">
                                Please select the account you want to sign into.
                              </p>
                            )}

                            <form key={1} onSubmit={handleSubmit(onSubmit)}>
                              {currentStep === LOGIN_STEPS.EMAIL && (
                                <>
                                  <InputFormField>
                                    <label htmlFor="emailAddress">
                                      Login with email
                                      <br />
                                      <input
                                        className="form-control"
                                        type="text"
                                        placeholder="Enter your Email Address"
                                        onChange={() => handleChange()}
                                        id="emailAddress"
                                        name="email"
                                        ref={register({
                                          required: {
                                            value: true,
                                            message: 'This field is required.',
                                          },
                                          pattern: {
                                            value:
                                              /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                                            message: 'Invalid email address.',
                                          },
                                        })}
                                      />
                                    </label>
                                  </InputFormField>
                                  <ErrorMsg>
                                    {errors?.email?.message ||
                                      customerApiError?.[0]}
                                  </ErrorMsg>
                                  <ErrorMsg>{forgotApiError}</ErrorMsg>
                                </>
                              )}

                              {currentStep ===
                                LOGIN_STEPS.ACCOUNT_SELECTION && (
                                <ContractInputSelect>
                                  <label htmlFor="account" className="mb-2">
                                    Select Account
                                    <Select
                                      options={customerNames}
                                      onChange={(event) =>
                                        setGetName({
                                          ...getName,
                                          customer: event.value,
                                        })
                                      }
                                      defaultValue={getName.customer}
                                    />
                                  </label>
                                </ContractInputSelect>
                              )}

                              {currentStep === LOGIN_STEPS.PASSWORD && (
                                <>
                                  <InputFormField>
                                    <label htmlFor="password">
                                      Enter password
                                      <br />
                                      <input
                                        className="form-control"
                                        type={
                                          isPasswordEyeOpen
                                            ? 'text'
                                            : 'password'
                                        }
                                        name="password"
                                        onChange={() => handleChange()}
                                        placeholder="Enter your Password"
                                        id="password"
                                        ref={register({
                                          required: {
                                            value: true,
                                            message: 'This field is required.',
                                          },
                                        })}
                                      />
                                      <ShowPasswordByIcon
                                        isPasswordEyeOpen={isPasswordEyeOpen}
                                        setIsPasswordEyeOpen={
                                          setIsPasswordEyeOpen
                                        }
                                        value={watch('password') || ''}
                                      />
                                    </label>
                                  </InputFormField>
                                  <ErrorMsg>
                                    {errors?.password?.message}
                                  </ErrorMsg>
                                  <ErrorMsg>
                                    {apiError?.data?.non_field_errors?.[0]}
                                  </ErrorMsg>
                                  <ErrorMsg>{forgotApiError}</ErrorMsg>
                                </>
                              )}

                              <Button
                                className="btn-primary w-100 mt-3 mb-3"
                                disabled={
                                  (currentStep ===
                                    LOGIN_STEPS.ACCOUNT_SELECTION &&
                                    getName.customer === '') ||
                                  (isLoading.loader &&
                                    isLoading.type === 'forgot')
                                }
                              >
                                {loader ||
                                (isLoading.loader &&
                                  isLoading.type === 'button') ? (
                                  <PageLoader color="#fff" type="button" />
                                ) : (
                                  <>
                                    {currentStep === LOGIN_STEPS.PASSWORD
                                      ? 'Sign In'
                                      : 'Continue'}
                                  </>
                                )}
                              </Button>

                              {/* Forgot Password Link */}
                              {currentStep === LOGIN_STEPS.PASSWORD && (
                                <span
                                  className="forgot-pswd-link cursor"
                                  onClick={() => forgotPassword()}
                                  role="presentation"
                                >
                                  Forgot password?
                                </span>
                              )}
                            </form>
                          </>
                        );

                      default:
                        return null;
                    }
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </FormContainer>
    </>
  );
}
